/**
 * C.8 — Vault search index.
 *
 * Builds a Fuse.js index over the vault's parsed notes. Supports:
 *   - fuzzy match on note name (highest weight)
 *   - fuzzy match on path
 *   - exact-prefix match on tags
 *   - substring match on body (lowest weight, capped at 4 KB per note
 *     to keep the index small)
 *
 * Result rows include a `score` (Fuse semantic: 0 = perfect, 1 =
 * miss) so callers can sort + threshold.
 *
 * Serialization: Fuse exposes `index.toJSON()` so the warm index can be
 * persisted to IndexedDB and rehydrated via `Fuse.parseIndex`.
 */

import Fuse, {
  type FuseIndex,
  type FuseResult,
  type IFuseOptions,
} from 'fuse.js';
import type { ParsedNote } from './parse';

const BODY_CAP = 4096;

export interface SearchableNote {
  path: string;
  name: string;
  pathSegments: string;
  tagsBlob: string;
  body: string;
  frontmatterBlob: string;
}

export interface SearchHit {
  path: string;
  name: string;
  score: number;
  matchedFields: string[];
}

const FUSE_OPTIONS: IFuseOptions<SearchableNote> = {
  includeScore: true,
  includeMatches: true,
  ignoreLocation: true,
  threshold: 0.4,
  minMatchCharLength: 2,
  shouldSort: true,
  keys: [
    { name: 'name', weight: 0.5 },
    { name: 'pathSegments', weight: 0.2 },
    { name: 'tagsBlob', weight: 0.15 },
    { name: 'frontmatterBlob', weight: 0.1 },
    { name: 'body', weight: 0.05 },
  ],
};

function toSearchable(n: ParsedNote): SearchableNote {
  // Truncate body so the persisted index stays under a few hundred KB
  // for a 100-note vault. Body is intentionally low-weight; the user is
  // more likely to find notes by name + tags than by content.
  const body = n.body.length > BODY_CAP ? n.body.slice(0, BODY_CAP) : n.body;
  const fmEntries = Object.entries(n.frontmatter ?? {}).map(
    ([k, v]) => `${k}:${typeof v === 'string' ? v : JSON.stringify(v)}`,
  );
  return {
    path: n.path,
    name: n.name,
    pathSegments: n.path.replace(/[/.\\]/g, ' '),
    tagsBlob: n.tags.map((t) => `#${t}`).join(' '),
    body,
    frontmatterBlob: fmEntries.join(' '),
  };
}

export interface VaultSearchIndex {
  query(q: string, limit?: number): SearchHit[];
  serialize(): { items: SearchableNote[]; index: unknown };
}

export function buildSearchIndex(notes: ParsedNote[]): VaultSearchIndex {
  const items = notes.map(toSearchable);
  const fuseKeys = FUSE_OPTIONS.keys ?? [];
  const fuseIndex = Fuse.createIndex(fuseKeys, items);
  const fuse = new Fuse(items, FUSE_OPTIONS, fuseIndex);
  return makeFromInternals(items, fuse, fuseIndex);
}

/**
 * Rehydrate a previously-serialized index. Returns null if the input
 * shape is unrecognized (older cache version, corrupt JSON, etc.).
 */
export function loadSearchIndex(
  serialized: { items: SearchableNote[]; index: unknown } | null,
): VaultSearchIndex | null {
  if (!serialized) return null;
  if (!Array.isArray(serialized.items)) return null;
  try {
    const fuseIndex = Fuse.parseIndex(
      serialized.index as Parameters<typeof Fuse.parseIndex>[0],
    ) as FuseIndex<SearchableNote>;
    const fuse = new Fuse(serialized.items, FUSE_OPTIONS, fuseIndex);
    return makeFromInternals(serialized.items, fuse, fuseIndex);
  } catch {
    return null;
  }
}

function makeFromInternals(
  items: SearchableNote[],
  fuse: Fuse<SearchableNote>,
  fuseIndex: FuseIndex<SearchableNote>,
): VaultSearchIndex {
  return {
    query(q: string, limit = 12): SearchHit[] {
      const trimmed = q.trim();
      if (!trimmed) return [];
      // Tag-prefixed queries (`#foo`) match the tags blob with stricter
      // threshold — users use #tag to filter, they want tight matches.
      const isTagQuery = trimmed.startsWith('#');
      const fuseInstance = isTagQuery
        ? new Fuse(items, { ...FUSE_OPTIONS, threshold: 0.2, keys: ['tagsBlob'] })
        : fuse;
      const results = fuseInstance.search(trimmed, { limit });
      return results.map((r: FuseResult<SearchableNote>) => ({
        path: r.item.path,
        name: r.item.name,
        score: r.score ?? 1,
        matchedFields: (r.matches ?? []).map((m) => m.key ?? '').filter(Boolean),
      }));
    },
    serialize() {
      return { items, index: fuseIndex.toJSON() };
    },
  };
}
